import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/contexts/UserContext';
import { toast } from '@/hooks/use-toast';

export interface PaymentSchedule {
  id: string;
  deal_id: string;
  milestone_name: string;
  amount: number;
  due_date: string;
  status: 'pending' | 'paid' | 'overdue';
  payment_date?: string;
  payment_method?: string;
  reference_number?: string;
  notes?: string;
  created_at: string;
  updated_at: string;
}

export const usePaymentSchedules = (dealId?: string) => {
  const [paymentSchedules, setPaymentSchedules] = useState<PaymentSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { currentUser } = useUser();

  const fetchPaymentSchedules = async () => {
    if (!currentUser?.id) return;
    
    setLoading(true);
    setError(null);

    try {
      let query = supabase
        .from('payment_schedules')
        .select('*')
        .order('due_date', { ascending: true });

      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Update overdue status based on current date
      const today = new Date().toISOString().split('T')[0];
      const updatedData = data?.map(schedule => ({
        ...schedule,
        status: schedule.status === 'pending' && schedule.due_date < today ? 'overdue' : schedule.status
      })) || [];

      setPaymentSchedules(updatedData as PaymentSchedule[]);
    } catch (err) {
      console.error('Error fetching payment schedules:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch payment schedules');
    } finally {
      setLoading(false);
    }
  };

  const createPaymentSchedule = async (scheduleData: Omit<PaymentSchedule, 'id' | 'created_at' | 'updated_at'>) => {
    try {
      const { data, error } = await supabase
        .from('payment_schedules')
        .insert([scheduleData])
        .select()
        .single();

      if (error) throw error;

      setPaymentSchedules(prev => [...prev, data as PaymentSchedule]);
      toast({
        title: "Success",
        description: "Payment schedule created successfully",
      });

      return data;
    } catch (err) {
      console.error('Error creating payment schedule:', err);
      toast({
        title: "Error",
        description: "Failed to create payment schedule",
        variant: "destructive",
      });
      throw err;
    }
  };

  const updatePaymentSchedule = async (scheduleId: string, updates: Partial<PaymentSchedule>) => {
    try {
      const { data, error } = await supabase
        .from('payment_schedules')
        .update(updates)
        .eq('id', scheduleId)
        .select()
        .single();

      if (error) throw error;

      setPaymentSchedules(prev => 
        prev.map(schedule => schedule.id === scheduleId ? data as PaymentSchedule : schedule)
      );

      toast({
        title: "Success", 
        description: "Payment schedule updated successfully",
      });

      return data;
    } catch (err) {
      console.error('Error updating payment schedule:', err);
      toast({
        title: "Error",
        description: "Failed to update payment schedule",
        variant: "destructive",
      });
      throw err;
    }
  };

  const markPaymentPaid = async (scheduleId: string, paymentDetails: {
    payment_date: string;
    payment_method?: string;
    reference_number?: string;
    notes?: string;
  }) => {
    return updatePaymentSchedule(scheduleId, {
      status: 'paid',
      ...paymentDetails
    });
  };

  const getPaymentSummary = () => {
    const totalAmount = paymentSchedules.reduce((sum, schedule) => sum + schedule.amount, 0);
    const paidAmount = paymentSchedules
      .filter(schedule => schedule.status === 'paid')
      .reduce((sum, schedule) => sum + schedule.amount, 0);
    const pendingAmount = paymentSchedules
      .filter(schedule => schedule.status === 'pending')
      .reduce((sum, schedule) => sum + schedule.amount, 0);
    const overdueAmount = paymentSchedules
      .filter(schedule => schedule.status === 'overdue')
      .reduce((sum, schedule) => sum + schedule.amount, 0);

    return {
      totalAmount,
      paidAmount,
      pendingAmount,
      overdueAmount,
      paidCount: paymentSchedules.filter(s => s.status === 'paid').length,
      pendingCount: paymentSchedules.filter(s => s.status === 'pending').length,
      overdueCount: paymentSchedules.filter(s => s.status === 'overdue').length,
    };
  };

  useEffect(() => {
    fetchPaymentSchedules();
  }, [currentUser?.id, dealId]);

  return {
    paymentSchedules,
    loading,
    error,
    fetchPaymentSchedules,
    createPaymentSchedule,
    updatePaymentSchedule,
    markPaymentPaid,
    getPaymentSummary: getPaymentSummary(),
  };
};